<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { BAlert, BButton, BButtonGroup } from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import router from '@/router';
import { formatDateTime } from '@/utils/date-utils';
import UserMfaDelete from '@/components/user/UserMfaDelete.vue';
import { MFAItem, KCCredentialDescriptor } from '@/types/auth-user';

const userStore = useUserStore();

// --- Local state  --
const mfaPassword = ref<MFAItem | null>(null);
const mfaTotp = ref<MFAItem | null>(null);
const mfaWebAuthns = ref<MFAItem[]>([]);

const credentials = ref<KCCredentialDescriptor[]>([]);

// Loading and Error States
const mfaFetchInProgress = ref(false);
const mfaFetchError = ref<string | null>(null);
const mfaToDelete = ref<MFAItem | null>(null);

// --- Delete MFA Credential ---
function credentialDeleteRequested(cred: MFAItem) {
  mfaToDelete.value = cred;
}

// --- RE-FETCH DATA WHEN VIEW ACTIVATED ---
async function refetchCredentials() {
  mfaToDelete.value = null;
  mfaFetchInProgress.value = true;
  mfaFetchError.value = null;

  try {
    credentials.value = await userStore.fetchUserCredentials();
    credentials.value.forEach((row) => {
      // password
      if (row.type === 'password') {
        if (row.userCredentialMetadatas.length > 0) {
          const c = row.userCredentialMetadatas[0].credential;
          mfaPassword.value = {
            kind: 'password',
            id: c.id,
            label: 'Password',
            createdAt: c.createdDate ? new Date(c.createdDate) : undefined,
            removeable: row.removeable,
            category: row.category,
          };
        } else {
          mfaPassword.value = null; // should not happen
        }
      }
      // totp
      else if (row.type === 'otp') {
        if (row.userCredentialMetadatas.length > 0) {
          const c = row.userCredentialMetadatas[0].credential;
          mfaTotp.value = {
            kind: 'otp',
            id: c.id,
            label: c.userLabel ?? 'Authenticator App',
            createdAt: c.createdDate ? new Date(c.createdDate) : undefined,
            removeable: row.removeable,
            category: row.category,
          };
        } else {
          mfaTotp.value = null;
        }
      }
      // webauthn
      else if (row.type === 'webauthn') {
        mfaWebAuthns.value = row.userCredentialMetadatas.map((meta) => {
          const c = meta.credential;
          return {
            kind: 'webauthn',
            id: c.id,
            label: c.userLabel ?? 'Security Key',
            createdAt: c.createdDate ? new Date(c.createdDate) : undefined,
            removeable: row.removeable,
            category: row.category,
          };
        });
      }
    });

    console.debug('User credentials:', credentials.value);
  } catch (err) {
    console.error('Error fetching credentials:', err);
    mfaFetchError.value = `Error fetching credentials: ${err}`;
  } finally {
    mfaFetchInProgress.value = false;
  }

  // Redirect to /mfa if no MFA methods are configured
  if (
    process.env.VUE_APP_REQUIRED_MFA === 'true' &&
    userStore.mfaNotConfigured
  ) {
    console.debug('No MFA methods configured, redirecting to /mfa');
    router.push({ name: 'MfaSelection' });
  }
}

onMounted(() => {
  refetchCredentials();
});
</script>

<template>
  <div>
    <!-- MFA Fetch Error -->
    <b-alert v-model="mfaFetchError" variant="danger" class="mt-3">
      <strong>Error obtaining MFA settings:</strong> {{ mfaFetchError }}
    </b-alert>

    <!-- Password -->
    <h5>
      <i class="material-symbols-outlined me-1">password</i>
      Password
      <i
        v-if="!mfaFetchInProgress"
        :class="{
          fa: true,
          'fa-check': mfaPassword !== null,
          'text-success': mfaPassword !== null,
          'fa-triangle-exclamation': !mfaPassword,
          'text-danger': !mfaPassword,
          'ms-1': true,
        }"
      ></i>
    </h5>

    <p v-if="mfaPassword && mfaPassword.createdAt" class="mb-1">
      <strong>Last updated: </strong>
      {{ formatDateTime(mfaPassword.createdAt) }}
    </p>

    <b-button
      variant="outline-dark"
      size="sm"
      @click="userStore.configureMfa('UPDATE_PASSWORD')"
    >
      <i class="fa fa-rotate me-1"></i>
      Update Password
    </b-button>

    <!-- WebAuthn: Passkey + Hardware key-->
    <h5 class="mt-3">
      <i class="fa fa-key me-1"></i>
      Passkey (WebAuthn)
      <i
        v-if="!mfaFetchInProgress && mfaWebAuthns.length > 0"
        class="fa fa-check text-success ms-1"
      ></i>
    </h5>

    <p class="mb-2">
      <strong>Recommended:</strong> Use a security key, hardware token or
      built-in biometric authentication (e.g. Touch ID, Face ID) for the highest
      level of security and convenience.
    </p>

    <!-- List of WebAuth items -->
    <div v-for="(webAuthn, idx) in mfaWebAuthns" :key="idx" class="mb-0 mt-0">
      <p class="mb-1 d-flex align-items-center">
        <i class="material-symbols-outlined me-1">qr_code_2</i>
        <span class="fw-bold me-1">{{ webAuthn.label }}</span>
        <span v-if="webAuthn.createdAt" class="text-secondary me-2">
          Added on: {{ formatDateTime(webAuthn.createdAt) }}
        </span>
        <b-button
          v-if="webAuthn.removeable"
          variant="outline-danger"
          size="sm"
          class="py-0 px-1"
          @click="credentialDeleteRequested(webAuthn)"
          :disabled="mfaToDelete !== null"
        >
          <i class="fa fa-times me-1"></i>Remove
        </b-button>
      </p>
    </div>

    <b-button
      id="webauthn-button"
      variant="outline-dark"
      class="me-2 mb-2 mt-2"
      size="sm"
      @click="userStore.configureMfa('webauthn-register')"
    >
      <i class="fa fa-plus me-1"></i> Add passkey
    </b-button>

    <!-- TOTP: Authenticator App -->
    <h5 class="mt-3">
      <i class="material-symbols-outlined me-1">lock_clock</i>
      Authenticator App (TOTP)
      <i
        v-if="!mfaFetchInProgress && mfaTotp !== null"
        class="fa fa-check text-success ms-1"
      ></i>
    </h5>

    <p>
      Use an authenticator app (e.g. FreeOTP, Google Authenticator, Microsoft
      Authenticator or your password manager) to generate
      <strong>time-based one-time passwords (TOTP)</strong>
      for secure access.
    </p>

    <p v-if="mfaTotp" class="d-flex align-items-center">
      <i class="material-symbols-outlined me-1">qr_code_2</i>
      <span class="fw-bold me-1">{{ mfaTotp.label }}</span>
      <span v-if="mfaTotp.createdAt" class="text-secondary me-2">
        Added on: {{ formatDateTime(mfaTotp.createdAt) }}
      </span>
      <b-button-group>
        <b-button
          variant="outline-dark"
          size="sm"
          class="py-0 px-1"
          @click="userStore.configureMfa('CONFIGURE_TOTP')"
        >
          <i class="fa fa-rotate me-1"></i>
          Reset TOTP
        </b-button>
        <b-button
          v-if="mfaTotp.removeable"
          variant="outline-danger"
          @click="credentialDeleteRequested(mfaTotp)"
          :disabled="mfaToDelete !== null"
          size="sm"
          class="py-0 px-1"
        >
          <i class="fa fa-times me-1"></i>Remove
        </b-button>
      </b-button-group>
    </p>

    <b-button
      v-if="!mfaTotp"
      variant="outline-dark"
      class="me-2 mb-2"
      size="sm"
      @click="userStore.configureMfa('CONFIGURE_TOTP')"
    >
      <i class="fa fa-plus me-1"></i>
      Set TOTP
    </b-button>

    <!-- MFA Delete Modal Dialog -->
    <UserMfaDelete
      v-if="mfaToDelete"
      :credentialId="mfaToDelete.id"
      :credentialLabel="mfaToDelete.label"
      :credentialType="
        mfaToDelete.kind === 'otp'
          ? 'TOTP'
          : mfaToDelete.kind === 'webauthn'
          ? 'Passkey'
          : mfaToDelete.kind
      "
      :show="mfaToDelete !== null"
      @close="mfaToDelete = null"
      @closeAndRefresh="refetchCredentials()"
    />
  </div>
</template>
<style scoped></style>
